import { useState } from "preact/compat"
import { useAsyncEffect } from "@/utils"
import {
  IntegrationWithJobInfo,
  getIntegrationsWithJobs,
  findIntegrationByName,
} from "@/api/integration"
import { LoadingSpinner } from "@/app/loading"
import { Button } from "@/components/ui/button"
import { useLocation } from "wouter-preact"
import { SettingsLayout } from "@/settings/layout"
import { IntegrationSettingsLayout, SettingsSection } from "../layout"
import candsLogo from "/img/integration/cands.png"
import { SyncStatusDisplay } from "../sync-status-row"
import { UpdateCredentialsForm } from "../update-credentials-form"
import { ContractsList } from "@/contracts/contracts-list"
import { CandsUcdClaimsForm } from "./ucd-claims-form"
import { CandsUploadSection } from "../cands-upload-section"

export function CandsSettingsPage() {
  const [, setLocation] = useLocation()
  const [candsIntegration, setCandsIntegration] = useState<IntegrationWithJobInfo | null>(null)
  const [disputeIntegration, setDisputeIntegration] = useState<IntegrationWithJobInfo | null>(null)
  const [emailAccounts, setEmailAccounts] = useState<IntegrationWithJobInfo[]>([])
  const [loading, setLoading] = useState(true)

  async function refreshIntegrations() {
    const res = await getIntegrationsWithJobs()
    if (res.ok) {
      const integrations = res.value.data
      setCandsIntegration(findIntegrationByName(integrations, "cands") ?? null)
      setDisputeIntegration(findIntegrationByName(integrations, "cands-dispute-portal") ?? null)
      setEmailAccounts(integrations.filter((i: IntegrationWithJobInfo) => ["gmail", "microsoft"].includes(i.name)))
    }
  }

  useAsyncEffect(async () => {
    await refreshIntegrations()
    setLoading(false)
  }, [])

  if (loading) {
    return (
      <SettingsLayout>
        <div class="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      </SettingsLayout>
    )
  }

  if (!candsIntegration) {
    return (
      <SettingsLayout>
        <div class="text-center py-12">
          <p class="text-gray-500 mb-4">C&S integration not found</p>
          <Button variant="outline" onClick={() => setLocation("/settings/connections")}>
            Back to Connections
          </Button>
        </div>
      </SettingsLayout>
    )
  }

  return (
    <IntegrationSettingsLayout
      logo={candsLogo}
      name="C&S Family of Companies"
      username={candsIntegration.username}
      integrationId={candsIntegration.id}>
      <SettingsSection title="Sync Status">
        <SyncStatusDisplay lastSyncJob={candsIntegration.last_sync_job} integrationName="cands" />
      </SettingsSection>

      <SettingsSection title="Upload Check Cover Sheets report">
        <CandsUploadSection integrationId={candsIntegration.id} onUploaded={refreshIntegrations} />
      </SettingsSection>

      <SettingsSection title="Vendor Portal Credentials">
        <p class="text-sm text-gray-500 mb-4">
          Credentials for{" "}
          <a
            class="underline text-gray-700"
            href="https://vendorportal.cswg.com/"
            target="_blank"
            rel="noopener noreferrer">
            vendorportal.cswg.com
          </a>
        </p>
        <UpdateCredentialsForm
          integrationName="cands"
          integrationId={candsIntegration.id}
          initialUsername={candsIntegration.username}
          credentialUpdatedAt={candsIntegration.credential_updated_at}
          onSuccess={refreshIntegrations}
        />
      </SettingsSection>

      <SettingsSection title="Dispute Portal (VCM) Credentials">
        <p class="text-sm text-gray-500 mb-4">
          Credentials for{" "}
          <a
            class="underline text-gray-700"
            href="https://vcm.cswg.com/"
            target="_blank"
            rel="noopener noreferrer">
            vcm.cswg.com
          </a>
          . Username may differ from the vendor portal.
        </p>
        {disputeIntegration ? (
          <UpdateCredentialsForm
            integrationName="cands-dispute-portal"
            integrationId={disputeIntegration.id}
            initialUsername={disputeIntegration.username}
            credentialUpdatedAt={disputeIntegration.credential_updated_at}
            onSuccess={refreshIntegrations}
          />
        ) : (
          <div class="flex items-center justify-between gap-4">
            <p class="text-sm text-gray-400">Dispute portal not connected</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setLocation("/settings/connect/cands-dispute-portal")}>
              Connect Dispute Portal
            </Button>
          </div>
        )}
      </SettingsSection>

      <SettingsSection title="Early Pay (UCD) Claims">
        <p class="text-sm text-gray-500 mb-4">
          C&S reviews unearned cash discount claims by email rather than in the dispute portal. Early pay disputes are
          batched into a claim sheet and sent to UCDclaims@cswg.com, up to 10 invoices per file.
        </p>
        <CandsUcdClaimsForm
          integration={candsIntegration}
          emailAccounts={emailAccounts}
          onSuccess={refreshIntegrations}
        />
      </SettingsSection>

      <SettingsSection title="Contract Documents">
        <ContractsList source="cands" integrationId={candsIntegration.id} />
      </SettingsSection>
    </IntegrationSettingsLayout>
  )
}
